import { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { getCronogramaPublico } from '@/services/cronogramasApi';
import type { Cronograma, CronogramaAtividadeStatus } from '@/types';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Skeleton } from '@/components/ui/skeleton';

const POLL_INTERVAL_MS = 30_000;

function fmtDate(val?: string | null) {
  if (!val) return '—';
  return new Date(val + 'T00:00:00').toLocaleDateString('pt-BR');
}

const STATUS_CONFIG: Record<CronogramaAtividadeStatus, { label: string; color: string; bg: string }> = {
  nao_iniciado: { label: 'Não iniciado', color: 'text-slate-600',   bg: 'bg-slate-200' },
  em_processo:  { label: 'Em processo',  color: 'text-amber-800',   bg: 'bg-amber-200' },
  finalizado:   { label: 'Finalizado',   color: 'text-emerald-800', bg: 'bg-emerald-200' },
  suspenso:     { label: 'Suspenso',     color: 'text-rose-800',    bg: 'bg-rose-200' },
};

function StatusBadge({ status }: { status: CronogramaAtividadeStatus }) {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.nao_iniciado;
  return (
    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium whitespace-nowrap ${cfg.bg} ${cfg.color}`}>
      {cfg.label}
    </span>
  );
}

function EmpresaAvatar({ nome }: { nome: string }) {
  const initials = nome
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map(w => w[0].toUpperCase())
    .join('');
  return (
    <div className="h-10 w-10 rounded-xl bg-indigo-600 flex items-center justify-center text-white font-bold text-sm shrink-0">
      {initials}
    </div>
  );
}

type CronogramaPublicoData = Cronograma & {
  empresa_nome: string | null;
  progresso: number;
  total_atividades: number;
  dashboard: { total: number; porStatus: Array<{ status: string; count: number; percentual: number }> };
};

export default function CronogramaPublico() {
  const { token } = useParams<{ token: string }>();
  const [data, setData] = useState<CronogramaPublicoData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdate, setLastUpdate] = useState<Date | null>(null);

  const fetchData = useCallback(async () => {
    if (!token) return;
    const res = await getCronogramaPublico(token);
    if (res.success) {
      setData(res.data as CronogramaPublicoData);
      setLastUpdate(new Date());
      setError(null);
    } else {
      setError(res.error || 'Link inválido ou expirado');
    }
    setLoading(false);
  }, [token]);

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [fetchData]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <div className="h-16 bg-indigo-600" />
        <div className="p-6 space-y-4 max-w-7xl mx-auto">
          <Skeleton className="h-20 w-full" />
          <Skeleton className="h-48 w-full" />
          <Skeleton className="h-48 w-full" />
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center space-y-2">
          <p className="text-xl font-semibold text-muted-foreground">Link inválido ou expirado</p>
          <p className="text-sm text-muted-foreground">Este link de compartilhamento não existe ou foi revogado.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Topbar da empresa */}
      <div className="bg-indigo-700 text-white">
        <div className="max-w-7xl mx-auto px-6 py-3 flex items-center gap-3">
          {data.empresa_nome && <EmpresaAvatar nome={data.empresa_nome} />}
          <div>
            {data.empresa_nome && (
              <p className="text-xs font-medium text-indigo-200 uppercase tracking-widest">Empresa</p>
            )}
            <p className="text-base font-semibold leading-tight">{data.empresa_nome || 'Cronograma'}</p>
          </div>
        </div>
      </div>

      {/* Header do cronograma */}
      <div className="bg-white border-b shadow-sm">
        <div className="max-w-7xl mx-auto px-6 py-5 flex items-start justify-between gap-4">
          <div>
            <h1 className="text-xl font-bold tracking-tight">{data.titulo}</h1>
            <div className="flex items-center gap-2 mt-1.5 flex-wrap">
              <Badge variant="outline">{data.area}</Badge>
              {data.data_inicio && (
                <span className="text-sm text-muted-foreground">Início: {fmtDate(data.data_inicio)}</span>
              )}
            </div>
          </div>
          <div className="flex items-center gap-4 shrink-0">
            <div className="text-right">
              <p className="text-xs text-muted-foreground uppercase tracking-wide">Progresso</p>
              <p className="text-2xl font-bold text-indigo-600">{data.progresso}%</p>
            </div>
            <div className="flex flex-col items-end gap-1">
              <Progress value={data.progresso} className="w-36 h-2.5" />
              {lastUpdate && (
                <span className="text-[11px] text-muted-foreground">
                  Atualizado às {lastUpdate.toLocaleTimeString('pt-BR')}
                </span>
              )}
            </div>
          </div>
        </div>
      </div>

      <main className="max-w-7xl mx-auto p-6 space-y-6">
        {(data.secoes || []).length === 0 && (
          <p className="text-center text-muted-foreground py-12">Nenhuma seção cadastrada.</p>
        )}
        {(data.secoes || []).map(secao => (
          <Card key={secao.id} className="shadow-sm">
            <CardHeader className="pb-2">
              <CardTitle className="text-base">
                {secao.numero} — {secao.titulo}
                {(secao.data_inicio || secao.data_termino) && (
                  <span className="ml-3 text-xs font-normal text-muted-foreground">
                    {fmtDate(secao.data_inicio)} → {fmtDate(secao.data_termino)}
                  </span>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {(secao.atividades || []).length === 0 ? (
                <p className="text-sm text-muted-foreground py-2">Nenhuma atividade nesta seção.</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-16">Cód.</TableHead>
                      <TableHead>Atividade</TableHead>
                      <TableHead className="hidden md:table-cell">Detalhamento</TableHead>
                      <TableHead className="hidden sm:table-cell">Responsável</TableHead>
                      <TableHead className="hidden lg:table-cell w-24">Início</TableHead>
                      <TableHead className="hidden lg:table-cell w-24">Término</TableHead>
                      <TableHead className="w-28">Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(secao.atividades || []).map(atv => (
                      <TableRow key={atv.id}>
                        <TableCell className="font-mono text-xs">{atv.codigo}</TableCell>
                        <TableCell className="font-medium">{atv.titulo}</TableCell>
                        <TableCell className="hidden md:table-cell text-muted-foreground text-sm">
                          {atv.detalhamento || '—'}
                        </TableCell>
                        <TableCell className="hidden sm:table-cell text-sm">
                          {(atv.responsaveis && atv.responsaveis.length > 0)
                            ? atv.responsaveis.map(r => r.nome).join(', ')
                            : (atv.responsavel?.nome || '—')}
                        </TableCell>
                        <TableCell className="hidden lg:table-cell text-sm">{fmtDate(atv.data_inicio)}</TableCell>
                        <TableCell className="hidden lg:table-cell text-sm">{fmtDate(atv.data_termino)}</TableCell>
                        <TableCell><StatusBadge status={atv.status} /></TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        ))}
      </main>
    </div>
  );
}
